/**
 * WorkerRTWShareCode — applicant-facing Right to Work share-code card.
 *
 * Lets an applicant PROVIDE their Home Office share code and confirm it is for a
 * Right to Work check. It never verifies Right to Work — the submission stays
 * pending admin review. Identity/tenant come from the worker session token only.
 *
 * The card only appears when a Right to Work online check is still required for
 * this applicant (i.e. not already verified). It renders nothing otherwise.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Shield, CheckCircle, Clock, ExternalLink, Loader2, Info } from 'lucide-react';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

export default function WorkerRTWShareCode() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [shareCode, setShareCode] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('workerToken') : null;

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/worker/right-to-work`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setData(null); setLoading(false); return; }
      const body = await res.json();
      setData(body);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    const cleaned = shareCode.trim();
    if (!cleaned) { toast.error('Please enter your share code'); return; }
    if (!confirm) { toast.error('Please confirm this code is for a Right to Work check'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/worker/right-to-work/share-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ share_code: cleaned, confirm_rtw: true }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || 'Could not save your share code');
        return;
      }
      toast.success('Share code received — your employer will complete the check');
      setShareCode('');
      setConfirm(false);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !data) return null;
  // Once verified there is nothing for the applicant to do here.
  if (data.is_verified) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-3" data-testid="rtw-share-code-verified">
        <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-green-800">Right to Work verified</p>
          <p className="text-xs text-green-700">Your Right to Work check has been completed and verified.</p>
        </div>
      </div>
    );
  }

  const submission = data.submission;
  const isPending = submission && submission.status === 'pending_admin_review';

  return (
    <div className="rounded-2xl border border-indigo-200 bg-white shadow-sm overflow-hidden" data-testid="rtw-share-code-card">
      <div className="bg-indigo-50 px-4 py-3 flex items-center gap-3 border-b border-indigo-100">
        <div className="p-1.5 rounded-lg bg-indigo-100">
          <Shield className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-indigo-900">Right to Work — share code</p>
          <p className="text-xs text-indigo-700">Give your employer the code so they can run the official GOV.UK check.</p>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {isPending && !showForm ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3" data-testid="rtw-share-code-pending">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              <p className="text-sm font-medium text-amber-800">Share code received — awaiting employer check</p>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              Code on file: <span className="font-mono">{submission.share_code_masked || 'provided'}</span>. Your employer
              will complete the Home Office check and record the result. You do not need to do anything else.
            </p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-2 text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
              data-testid="rtw-share-code-update"
            >
              Provide a different share code
            </button>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-start gap-2">
              <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-600">
                Get your share code from the GOV.UK service, then enter it below. Your employer performs the
                actual Right to Work check on GOV.UK — entering a code here does not verify your status.
              </p>
            </div>
            <a
              href="https://www.gov.uk/prove-right-to-work"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-medium text-indigo-700 hover:text-indigo-900"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Get a share code on GOV.UK
            </a>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Home Office share code</label>
              <input
                type="text"
                value={shareCode}
                onChange={(e) => setShareCode(e.target.value.toUpperCase())}
                placeholder="e.g. W3F 5RT 9AB"
                data-testid="rtw-share-code-input"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono uppercase focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                data-testid="rtw-share-code-confirm"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs text-slate-700">
                I confirm this share code is for a <strong>Right to Work</strong> check.
              </span>
            </label>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="rtw-share-code-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                Submit share code
              </button>
              {isPending && (
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setShareCode(''); setConfirm(false); }}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
